import {onLayoutChange} from "./layout";

type Direction = -1 | 0 | 1;

type VisibilityCallback = (
    element: HTMLElement,
    direction: Direction,
) => void;

type Progress = {
    innerProgress: number | undefined,
    outerProgress: number,
    upperProgress: number,
    lowerProgress: number,
    introProgress: number,
    outroProgress: number,
    introShortProgress: number,
};

type ProgressCallback = (
    element: HTMLElement,
    progress: Progress,
    direction: Direction,
) => void;

interface Watched {
    element: HTMLElement,
    onAppearing?: VisibilityCallback,
    onAppeared?: VisibilityCallback,
    onDisappearing?: VisibilityCallback,
    onDisappeared?: VisibilityCallback,
    /** progress range: 0-1
     * @param innerProgress: komplett sichtbar bis anfängt zu verschwinden
     * @param outerProgress: sobald 1 px sichtbar ist bis es gar nicht mehr sichtbar ist
     * @param upperProgress: Fortschritt der Oberkante des Elements im Viewport-Bereich
     * @param lowerProgress: Fortschritt der Unterkante des Elements im Viewport-Bereich
     * @param introProgress: Fortschritt zwischen 'Start Verschwinden der Oberkante' und 'Element nicht mehr sichtbar'
     * @param outroProgress: Fortschritt zwischen 'Auftauchen der Oberkante' und 'Ankommen der Unterkante am unteren Viewport-Rand'
     * @param introShortProgress: Fortschritt zwischen 'Start Verschwinden der Oberkante' und 'Ankommen der Unterkante am unteren Viewport-Rand'
     *
    */
    onProgress?: ProgressCallback,
}

enum State {
    UNINITIALIZED, NOT_VISIBLE, PARTIALLY_VISIBLE, FULLY_VISIBLE,
}

type Item = [Watched, State];

const scroller = document.scrollingElement ?? document.documentElement;

class Watcher {
    private items: Array<Item>;
    private previousScrollY: number;

    constructor() {
        this.items = [];
        this.previousScrollY = scroller.scrollTop;

        const update = () => this.handleScroll(this.items);
        window.addEventListener('scroll', update);
        // onLayoutChange(update);
    }

    private handleScroll(items: Array<Item>) {
        const viewportTop = 0,
              viewportBottom = viewportTop + scroller.clientHeight;
        const currentScrollY = scroller.scrollTop,
              previousScrollY = this.previousScrollY,
              direction = Math.sign(currentScrollY - previousScrollY) as Direction;
        const contentTop = viewportTop - currentScrollY,
              contentBottom = scroller.scrollHeight - currentScrollY;
        this.previousScrollY = currentScrollY;

        items.forEach(item => {
            const [watched, state] = item;
            const element = watched.element;
            const elementBbox = element.getBoundingClientRect();
            const elementTop = elementBbox.top,
                  elementBottom = elementBbox.bottom;
            const topVisible = elementTop >= viewportTop && elementTop < viewportBottom,
                  bottomVisible = elementBottom >= viewportTop && elementBottom < viewportBottom;
            const newState =
                topVisible && bottomVisible ? State.FULLY_VISIBLE :
                topVisible || bottomVisible ? State.PARTIALLY_VISIBLE :
                elementBottom < viewportTop || elementTop >= viewportBottom ? State.NOT_VISIBLE :
                State.PARTIALLY_VISIBLE;
            if(newState != State.NOT_VISIBLE && watched.onProgress) {
                const viewportHeight = viewportBottom - viewportTop,
                        elementHeight = elementBottom - elementTop;
                // Fortschritt in dem Bereich, wo das Element vollständig sichtbar ist
                // (d.h. oberer und unterer Rand sind innerhalb vom Viewport)
                const innerProgress = viewportHeight <= elementHeight ? undefined :
                    Math.min(1, Math.max(0,
                        1 - (elementTop - viewportTop) / (viewportHeight - elementHeight)
                    ));
                // Fortschritt vom Auftauchen der Spitze am unteren Viewport-Rand bis
                // zum Verschwinden des Bodens am oberen Viewport-Rand)
                const outerProgress =
                    Math.min(1, Math.max(0,
                        1 - (elementBottom - viewportTop) / (viewportHeight + elementHeight)
                    ));
                // Fortschritt der Oberkante des Elements im Viewport-Bereich:
                const upperProgress =
                    Math.min(1, Math.max(0,
                        1 - (elementTop - viewportTop) / viewportHeight
                    ));
                // Fortschritt der Unterkante des Elements im Viewport-Bereich:
                const lowerProgress =
                    Math.min(1, Math.max(0,
                        1 - (elementBottom - viewportTop) / viewportHeight
                    ));
                // Fortschritt zwischen 'Start Verschwinden der Oberkante' und 'Element nicht mehr sichtbar':
                const introProgress =
                    Math.min(1, Math.max(0,
                        1 - (elementBottom - viewportTop) / elementHeight
                    ));
                // Fortschritt zwischen 'Auftauchen der Oberkante' und 'Ankommen der Unterkante am unteren Viewport-Rand'
                const outroProgress =
                Math.min(1, Math.max(0,
                    1 - (elementBottom - viewportBottom) / elementHeight
                ));
                // Fortschritt zwischen 'Start Verschwinden der Oberkante' und 'Ankommen der Unterkante am unteren Viewport-Rand':
                const introShortProgress =
                    Math.min(1, Math.max(0,
                        1 - (elementBottom - viewportBottom) / (elementHeight - viewportHeight)
                    ));

                watched.onProgress(element, {
                    innerProgress,
                    outerProgress,
                    upperProgress,
                    lowerProgress,
                    introProgress,
                    outroProgress,
                    introShortProgress,
                }, direction);
            }
            if(newState == state) return;
            switch(newState) {
            case State.NOT_VISIBLE:
                if(watched.onProgress) {
                    if(elementTop < viewportTop)
                        watched.onProgress(element, {
                            innerProgress: 1,
                            outerProgress: 1,
                            upperProgress: 1,
                            lowerProgress: 1,
                            introProgress: 1,
                            outroProgress: 1,
                            introShortProgress: 1,
                        }, direction);

                    else
                        watched.onProgress(element, {
                            innerProgress: 0,
                            outerProgress: 0,
                            upperProgress: 0,
                            lowerProgress: 0,
                            introProgress: 0,
                            outroProgress: 0,
                            introShortProgress: 0,
                        }, direction);
                }
                if(state == State.FULLY_VISIBLE) {
                    if(watched.onDisappearing)
                        watched.onDisappearing(element, direction);
                }
                if(watched.onDisappeared)
                    watched.onDisappeared(element, direction);
                break;
            case State.PARTIALLY_VISIBLE:
                if(state == State.UNINITIALIZED || state == State.NOT_VISIBLE) {
                    if(watched.onAppearing)
                        watched.onAppearing(element, direction);
                } else if(state == State.FULLY_VISIBLE) {
                    if(watched.onDisappearing)
                        watched.onDisappearing(element, direction);
                }
                break;
            case State.FULLY_VISIBLE:
                if(state == State.UNINITIALIZED || state == State.NOT_VISIBLE) {
                    if(watched.onAppearing)
                        watched.onAppearing(element, direction);
                }
                if(watched.onAppeared)
                    watched.onAppeared(element, direction);
                break;
            }
            item[1] = newState;
        });
    }

    add(what: Watched) {
        const item: Item = [what, State.UNINITIALIZED];
        this.items.push(item);
        this.handleScroll([item]);
    }

    refresh() {
        this.items.forEach(item => item[1] = State.UNINITIALIZED);
        this.handleScroll(this.items);
    }
}

// export {Watcher, Watched};
export default Watcher;
